/* ==========================================================================
   Single source of truth for the storefront's read-only catalogue calls.
   Everything here hits our own backend (server/) — there is no supplier
   feed and no demo/mock mode anymore.
   ========================================================================== */

export const BASE = process.env.REACT_APP_BASE_URL || "http://localhost:8000";

/** Turn a stored filename into a full URL to the uploaded image. */
export function imageUrl(filename) {
  if (!filename) return "";
  if (/^https?:\/\//i.test(filename)) return filename; // already a full URL
  return `${BASE}/images/${filename}`;
}

async function apiGet(path) {
  const res = await fetch(`${BASE}/api${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || "No pudimos completar la solicitud.");
  return data;
}

/** Backend record -> the shape every component in the app expects. */
export function normalize(item) {
  if (!item) return null;

  const images = (item.images || []).map(imageUrl);
  const hasDiscount = item.discountPrice && item.discountPrice > 0 && item.discountPrice < item.price;

  return {
    _id: item._id,
    sku: item.sku,
    brand: item.brand || "",
    title: item.title || "",
    description: item.description || "",
    category: item.category || "",
    condition: item.condition || "new",
    image: images[0] || "",
    images,
    price: hasDiscount ? item.discountPrice : item.price,
    originalPrice: hasDiscount ? item.price : null,
    stock: typeof item.stock === "number" ? item.stock : null,
    specs: item.specs || {},
    extraSpecs: item.extraSpecs || [],
    related: Array.isArray(item.related) ? item.related.map(normalize) : [],
  };
}

/* --------------------------------------------------------------------------
   Formatting
   -------------------------------------------------------------------------- */

export const euro = (value) =>
  new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/** Predefined specs, in the order a phone buyer scans them. */
export const specList = (specs = {}) =>
  [specs.storage, specs.ram && `${specs.ram} RAM`, specs.color, specs.network, specs.sim].filter(Boolean);

/* --------------------------------------------------------------------------
   Endpoints
   -------------------------------------------------------------------------- */

export async function fetchProducts(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) query.set(key, value);
  });

  const data = await apiGet(`/products?${query.toString()}`);
  return { ...data, items: (data.items || []).map(normalize) };
}

export async function fetchProduct(sku) {
  return normalize(await apiGet(`/products/${encodeURIComponent(sku)}`));
}

export async function fetchFilters() {
  return apiGet("/products/filters");
}
